// ── Shared Utilities for Testing Ground Redesign ──────────────────────────────
import type { Creds, IndividualForm } from './types'
import type { KycScenario } from './data'
import { DEFAULT_CREDS } from './data'

// ── API Proxy Helpers ─────────────────────────────────────────────────────────

export function proxyUrl(frankiePath: string): string {
  const v2Path = frankiePath.startsWith('/') ? frankiePath : `/${frankiePath}`
  return `/api/frankie-proxy${v2Path}`
}

export function buildHeaders(creds: Creds, withBody = false): Record<string, string> {
  const h: Record<string, string> = {
    'X-FT-Customer-ID': creds.customerId,
    'X-FT-API-Key': creds.apiKey,
    'X-FT-API-Base': creds.apiBase || DEFAULT_CREDS.apiBase,
    'Accept': 'application/json',
  }
  if (creds.childCustomerId) h['X-FT-Child-Customer-ID'] = creds.childCustomerId
  if (withBody) h['Content-Type'] = 'application/json'
  return h
}

export function buildMachineHeaders(creds: Creds, withBody = false): Record<string, string> {
  const h: Record<string, string> = {
    'X-FT-Customer-ID': creds.customerId,
    'X-FT-API-Key': creds.apiKey,
    'X-FT-Machine-Base': creds.machineBase || DEFAULT_CREDS.machineBase,
    'Accept': 'application/json',
  }
  if (withBody) h['Content-Type'] = 'application/json'
  return h
}

// ── Payload Builder ───────────────────────────────────────────────────────────

export function buildIndividual(form: IndividualForm) {
  const doc: Record<string, string> = {
    type: form.docType, class: 'IDENTITY', primaryIdentifier: form.docNumber, country: form.docCountry,
  }
  if (form.docSecondaryIdentifier.trim()) doc.secondaryIdentifier = form.docSecondaryIdentifier.trim()
  if (form.docType === 'DRIVERS_LICENSE' && form.docSubdivision.trim()) doc.subdivision = form.docSubdivision.trim()
  return {
    individual: {
      name: { givenName: form.givenName, ...(form.middleName.trim() ? { middleName: form.middleName.trim() } : {}), familyName: form.familyName },
      dateOfBirth: { year: form.dobYear, month: form.dobMonth, day: form.dobDay },
      nationality: form.nationality,
      addresses: [{ type: 'RESIDENTIAL', streetNumber: form.addressStreetNumber, streetName: form.addressStreetName, streetType: form.addressStreetType, locality: form.addressLocality, subdivision: form.addressSubdivision, postalCode: form.addressPostalCode, country: form.addressCountry }],
      documents: { IDENTITY: [doc] },
      consents: ([form.consentGeneral ? { type: 'GENERAL' } : null, form.consentDocs ? { type: 'DOCS' } : null] as ({ type: string } | null)[]).filter((x): x is { type: string } => x !== null),
      ...(form.externalRef ? { externalReferences: [{ type: 'CUSTOMER', name: 'customer-id', value: form.externalRef }] } : {}),
    },
  }
}

// ── Scenario → Form Mapper ────────────────────────────────────────────────────

export function scenarioToForm(s: KycScenario): IndividualForm {
  const ind = s.rawPayload.individual
  const name = (ind.name ?? {}) as Record<string, string>
  const dob = (ind.dateOfBirth ?? {}) as Record<string, string>
  const addr = ((ind.addresses ?? []) as Record<string, string>[])[0] ?? {}
  const doc = ((ind.documents as { IDENTITY?: Record<string, string>[] } | undefined)?.IDENTITY ?? [])[0] ?? {}
  return {
    givenName: name.givenName ?? '', middleName: name.middleName ?? '', familyName: name.familyName ?? '',
    dobYear: dob.year ?? '', dobMonth: dob.month ?? '', dobDay: dob.day ?? '',
    nationality: (ind.nationality as string) ?? 'AUS',
    addressStreetNumber: addr.streetNumber ?? '', addressStreetName: addr.streetName ?? '', addressStreetType: addr.streetType ?? '',
    addressLocality: addr.locality ?? '', addressSubdivision: addr.subdivision ?? '', addressPostalCode: addr.postalCode ?? '', addressCountry: addr.country ?? 'AUS',
    docType: doc.type ?? 'PASSPORT', docNumber: doc.primaryIdentifier ?? '', docSecondaryIdentifier: doc.secondaryIdentifier ?? '',
    docCountry: doc.country ?? 'AUS', docSubdivision: doc.subdivision ?? '',
    consentGeneral: true, consentDocs: true, externalRef: '',
  }
}

// ── Color Helpers ─────────────────────────────────────────────────────────────

export function resultColor(r?: string) {
  if (!r) return 'text-[#475569]'
  if (r === 'PASS' || r === 'CLEAR') return 'text-[#34d399]'
  if (r === 'FAIL') return 'text-[#f87171]'
  return 'text-[#fb923c]'
}

export function outcomeColors(result?: string): { bg: string; border: string; text: string; icon: string } {
  const r = (result ?? '').toUpperCase()
  if (r === 'PASS' || r === 'CLEAR') return { bg: 'rgba(52,211,153,.08)', border: 'rgba(52,211,153,.25)', text: '#86efac', icon: '#34d399' }
  if (r === 'FAIL') return { bg: 'rgba(248,113,113,.08)', border: 'rgba(248,113,113,.25)', text: '#fca5a5', icon: '#f87171' }
  return { bg: 'rgba(251,146,60,.08)', border: 'rgba(251,146,60,.25)', text: '#fdba74', icon: '#fb923c' }
}

// ── Credential localStorage ───────────────────────────────────────────────────

const CREDS_KEY = 'ft-credentials-dealhub'

export function loadCredsFromStorage(defaults: Creds): Creds {
  try {
    const stored = localStorage.getItem(CREDS_KEY)
    if (stored) return { ...defaults, ...JSON.parse(stored) as Partial<Creds> }
  } catch { /* ignore */ }
  return defaults
}

export function saveCredsToStorage(creds: Creds): void {
  try { localStorage.setItem(CREDS_KEY, JSON.stringify(creds)) } catch { /* ignore */ }
}

// ── Time Helpers ──────────────────────────────────────────────────────────────

export function relativeTime(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d
  const secs = Math.floor((Date.now() - date.getTime()) / 1000)
  if (secs < 10) return 'just now'
  if (secs < 60) return `${secs}s ago`
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  return `${Math.floor(secs / 3600)}h ago`
}

export function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch { return iso }
}

// ── Env Badge Helper ──────────────────────────────────────────────────────────

export function deriveEnv(apiBase: string): 'UAT' | 'PROD' | null {
  if (!apiBase) return null
  if (apiBase.includes('uat')) return 'UAT'
  if (apiBase.includes('api.frankie.one') || apiBase.includes('frankiefinancial')) return 'PROD'
  return null
}
